import type { KitBand, KitSpec } from '@/config/cosmetics';
import { oranje88 } from './kits';
import { css, luma, mixColor, rgba } from './types';

/**
 * livery/jersey.ts (M144) — the kit as a JERSEY, for the shop and customize
 * cards. Owner brief: "the shorts are not part of the design, it is the
 * jerseys; make them a bit more modern looking and less rigid."
 *
 * So the card is the shirt alone, drawn the way a modern product shot shows
 * one: floating at a slight tilt, soft-edged, with real cloth — a set-in
 * sleeve seam, folds off the armpits, a lit chest, a collar with its real
 * cut (crew, V, wrap-over, polo, open collar) and the back of the neck
 * visible through it. The pattern is the SAME KitSpec the car wears: stripes
 * run down the body and along each sleeve, hoops cross both, the chest band
 * sits where the real one sits, the collar and cuffs carry their bands.
 *
 * Drawn in "shirt units": the body is 2 wide (x −1..1), the collar sits at
 * y = 0 and the hem near y = 2.45.
 */

type G = CanvasRenderingContext2D;
type Pt = [number, number];

const HEM = 2.44;
/** Right-hand outline points (mirrored for the left). */
const NECK: Pt = [0.37, 0.03];
const SHOULDER: Pt = [1.05, 0.25];
const CUFF_TOP: Pt = [1.8, 0.91];
const CUFF_BOT: Pt = [1.5, 1.25];
const ARMPIT: Pt = [1.0, 0.91];
const WAIST: Pt = [0.94, 1.7];
const HIP: Pt = [0.99, HEM - 0.03];
const FAR = 9;

function neckDepth(spec: KitSpec): number {
  const neck = spec.neck ?? 'crew';
  return neck === 'vee' || neck === 'wrap' ? 0.5 : neck === 'open' ? 0.52 : neck === 'polo' ? 0.3 : 0.2;
}

/** The front neckline, viewer's left → right, as sample points. */
function necklinePts(spec: KitSpec): Pt[] {
  const d = neckDepth(spec);
  if ((spec.neck ?? 'crew') !== 'crew') return [[-NECK[0], NECK[1]], [0, d], [NECK[0], NECK[1]]];
  const pts: Pt[] = [];
  for (let i = 0; i <= 20; i++) {
    const a = (i / 20) * Math.PI;
    pts.push([-Math.cos(a) * NECK[0], NECK[1] + Math.sin(a) * (d - NECK[1])]);
  }
  return pts;
}

/** The whole shirt outline (clockwise from the viewer's left neck point). */
function outline(spec: KitSpec): Path2D {
  const p = new Path2D();
  const nl = necklinePts(spec);
  p.moveTo(nl[0][0], nl[0][1]);
  for (let i = 1; i < nl.length; i++) p.lineTo(nl[i][0], nl[i][1]);
  // right shoulder + sleeve
  p.quadraticCurveTo(0.74, 0.08, SHOULDER[0], SHOULDER[1]);
  p.quadraticCurveTo(1.5, 0.51, CUFF_TOP[0], CUFF_TOP[1]);
  p.lineTo(CUFF_BOT[0], CUFF_BOT[1]);
  p.quadraticCurveTo(1.2, 1.01, ARMPIT[0], ARMPIT[1]);
  // right side seam
  p.bezierCurveTo(0.97, 1.25, WAIST[0] - 0.02, 1.5, WAIST[0], WAIST[1]);
  p.quadraticCurveTo(0.95, 2.1, HIP[0], HIP[1]);
  // hem (a soft curve)
  p.quadraticCurveTo(0, HEM + 0.1, -HIP[0], HIP[1]);
  // left side, mirrored
  p.quadraticCurveTo(-0.95, 2.1, -WAIST[0], WAIST[1]);
  p.bezierCurveTo(-(WAIST[0] - 0.02), 1.5, -0.97, 1.25, -ARMPIT[0], ARMPIT[1]);
  p.quadraticCurveTo(-1.2, 1.01, -CUFF_BOT[0], CUFF_BOT[1]);
  p.lineTo(-CUFF_TOP[0], CUFF_TOP[1]);
  p.quadraticCurveTo(-1.5, 0.51, -SHOULDER[0], SHOULDER[1]);
  p.quadraticCurveTo(-0.74, 0.08, -NECK[0], NECK[1]);
  p.closePath();
  return p;
}

/** One sleeve: everything outside the set-in armhole seam. s = ±1. */
function sleevePath(s: number): Path2D {
  const p = new Path2D();
  p.moveTo(s * SHOULDER[0], SHOULDER[1]);
  p.quadraticCurveTo(s * 1.5, 0.51, s * CUFF_TOP[0], CUFF_TOP[1]);
  p.lineTo(s * CUFF_BOT[0], CUFF_BOT[1]);
  p.quadraticCurveTo(s * 1.2, 1.01, s * ARMPIT[0], ARMPIT[1]);
  // the armhole seam, bowing into the body
  p.quadraticCurveTo(s * 0.86, 0.57, s * SHOULDER[0], SHOULDER[1]);
  p.closePath();
  return p;
}

/** The body without its sleeves (armhole to armhole). */
function torsoClip(g: G, shirt: Path2D): void {
  g.clip(shirt);
  const p = new Path2D();
  p.moveTo(-SHOULDER[0], -1);
  p.lineTo(-SHOULDER[0], SHOULDER[1]);
  p.quadraticCurveTo(-0.86, 0.57, -ARMPIT[0], ARMPIT[1]);
  p.lineTo(-1.2, HEM + 1);
  p.lineTo(1.2, HEM + 1);
  p.lineTo(ARMPIT[0], ARMPIT[1]);
  p.quadraticCurveTo(0.86, 0.57, SHOULDER[0], SHOULDER[1]);
  p.lineTo(SHOULDER[0], -1);
  p.closePath();
  g.clip(p);
}

/** Along-the-sleeve frame: x runs across the sleeve, y from shoulder to cuff. */
function sleeveFrame(g: G, s: number): void {
  const ax = (CUFF_TOP[0] + CUFF_BOT[0]) / 2 - (SHOULDER[0] + ARMPIT[0]) / 2;
  const ay = (CUFF_TOP[1] + CUFF_BOT[1]) / 2 - (SHOULDER[1] + ARMPIT[1]) / 2;
  const ang = Math.atan2(ay, ax) - Math.PI / 2; // rotate "down" onto the sleeve axis
  g.translate((s * (SHOULDER[0] + ARMPIT[0])) / 2, (SHOULDER[1] + ARMPIT[1]) / 2);
  g.rotate(s * ang);
}

function vBands(g: G, origin: number, period: number, width: number): void {
  for (let k = -24; k <= 24; k++) g.fillRect(origin + k * period - width / 2, -FAR, width, 2 * FAR);
}
function hBands(g: G, origin: number, period: number, width: number): void {
  for (let k = -24; k <= 24; k++) g.fillRect(-FAR, origin + k * period - width / 2, 2 * FAR, width);
}

function starAt(g: G, cx: number, cy: number, r: number): void {
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    if (i === 0) g.moveTo(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
    else g.lineTo(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
  }
  g.closePath();
  g.fill();
}

/** The shirt's FIELD, body then sleeves. */
function paintField(g: G, spec: KitSpec, shirt: Path2D): void {
  const [base, pat] = spec.colors;
  g.save();
  g.clip(shirt);
  g.fillStyle = css(base);
  g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
  g.fillStyle = css(pat);
  /** Paint each sleeve: a solid sleeve colour, or `draw` in the sleeve frame. */
  const sleeves = (draw?: (s: number) => void): void => {
    for (const s of [-1, 1]) {
      g.save();
      g.clip(sleevePath(s));
      g.fillStyle = css(spec.sleeves ?? base);
      g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
      g.fillStyle = css(pat);
      if (spec.sleeves === undefined && draw) draw(s);
      g.restore();
    }
  };
  switch (spec.pattern) {
    case 'stripes': {
      const period = 2 / (spec.count ?? 5);
      const width = (spec.ratio ?? 0.5) * period;
      const off = (spec.centre ?? 'pattern') === 'pattern' ? 0 : period / 2;
      const lay = (): void => {
        g.fillStyle = css(pat);
        vBands(g, off, period, width);
        if (spec.pin !== undefined) {
          g.fillStyle = css(spec.pin);
          const t = Math.min(0.024, width * 0.09);
          vBands(g, off - width / 2 + t * 2.2, period, t);
          vBands(g, off + width / 2 - t * 2.2, period, t);
        }
      };
      lay();
      sleeves((s) => {
        sleeveFrame(g, s);
        lay();
      });
      break;
    }
    case 'hoops': {
      const period = HEM / (spec.count ?? 5);
      const width = (spec.ratio ?? 0.5) * period;
      const first = ((spec.top ?? 'pattern') === 'pattern' ? 0 : period / 2) + width / 2;
      hBands(g, first, period, width);
      // The sleeves hoop in line with the body.
      sleeves((s) => {
        sleeveFrame(g, s);
        hBands(g, first - 0.38, period, width);
      });
      break;
    }
    case 'sash': {
      // From the wearer's left shoulder (the viewer's top-right) to the hip.
      g.beginPath();
      g.moveTo(0.4, -0.1);
      g.lineTo(0.9, -0.1);
      g.lineTo(-0.56, HEM + 0.1);
      g.lineTo(-1.06, HEM + 0.1);
      g.closePath();
      g.fill();
      sleeves();
      break;
    }
    case 'quarters': {
      // The eight-piece shirt: halves, with each sleeve in the other colour.
      g.fillRect(0, -FAR, FAR, 2 * FAR);
      for (const s of [-1, 1]) {
        g.save();
        g.clip(sleevePath(s));
        g.fillStyle = css(s < 0 ? pat : base);
        g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
        g.restore();
      }
      break;
    }
    case 'chestband': {
      const b = spec.band ?? {};
      const y0 = 0.05 + (b.y ?? 0.2) * HEM;
      const h = (b.h ?? 0.2) * HEM;
      const lineColor = b.edge ?? pat;
      g.save();
      if (b.body) torsoClip(g, shirt);
      const half = b.body && b.lines ? 0.68 : FAR;
      g.fillStyle = css(pat);
      g.fillRect(-half, y0, 2 * half, h);
      if (b.lines) {
        g.fillStyle = css(lineColor);
        for (let i = 1; i <= b.lines; i++) g.fillRect(-half - i * 0.03, y0 + h + i * h * 1.25, 2 * (half + i * 0.03), 0.034);
      } else if (b.edge !== undefined) {
        g.fillStyle = css(b.edge);
        g.fillRect(-half, y0 - 0.085, 2 * half, 0.036);
        g.fillRect(-half, y0 + h + 0.05, 2 * half, 0.036);
      }
      if (spec.stars) {
        g.fillStyle = css(spec.stars.color);
        for (let i = 0; i < spec.stars.n; i++) starAt(g, 0.28 + i * 0.16, y0 - 0.13, 0.06);
      }
      g.restore();
      break;
    }
    case 'centreband': {
      const wd = (spec.width ?? 0.42) * 2;
      g.fillRect(-wd / 2, -FAR, wd, 2 * FAR);
      sleeves();
      break;
    }
    case 'solid':
    case 'dragon':
      sleeves();
      break;
  }

  // The yoke: bands across the shoulders, armhole to armhole.
  if (spec.yoke) {
    g.save();
    torsoClip(g, shirt);
    const total = spec.yoke.weights.reduce((a, b) => a + b, 0);
    const depth = HEM * 0.25;
    let at = 0;
    spec.yoke.colors.forEach((c, i) => {
      const t = (spec.yoke!.weights[i] / total) * depth;
      if (c !== null) {
        g.fillStyle = css(c);
        g.fillRect(-FAR, at, 2 * FAR, t);
      }
      at += t;
    });
    g.restore();
  }

  // Tonal weaves and prints.
  if (spec.shadow === 'oranje') {
    // The real 1988 print, at shirt scale: panel x = down, y = across.
    g.save();
    const k = 2 / 1.65;
    g.transform(0, k, k, 0, 0, 0.02);
    oranje88(g, base, -0.2, HEM / k + 0.2, 2);
    g.restore();
  } else if (spec.shadow === 'pinstripe') {
    g.fillStyle = rgba(0x000000, luma(base) > 0.7 ? 0.075 : 0.11);
    for (let x = -3; x < 3; x += 0.125) g.fillRect(x, -FAR, 0.024, 2 * FAR);
  } else if (spec.shadow === 'hoops') {
    g.fillStyle = rgba(0x000000, 0.08);
    for (let y = 0; y < 3; y += 0.16) g.fillRect(-FAR, y, 2 * FAR, 0.07);
  } else if (spec.shadow === 'diamonds') {
    const s = 0.3;
    for (let r = -2; r < 20; r++) {
      for (let c = -12; c < 12; c++) {
        const cx = (c + (r % 2 ? 0.5 : 0)) * s;
        const cy = r * s * 0.5;
        g.fillStyle = rgba(0x000000, 0.1);
        g.beginPath();
        g.moveTo(cx + s / 2, cy);
        g.lineTo(cx, cy + s / 2);
        g.lineTo(cx - s / 2, cy);
        g.closePath();
        g.fill();
      }
    }
  }

  // Raglan piping: neck → armpit.
  if (spec.piping !== undefined) {
    g.strokeStyle = css(spec.piping);
    g.lineWidth = 0.035;
    for (const s of [-1, 1]) {
      g.beginPath();
      g.moveTo(s * NECK[0] * 1.04, NECK[1] + 0.03);
      g.quadraticCurveTo(s * 0.74, 0.42, s * ARMPIT[0], ARMPIT[1]);
      g.stroke();
    }
  }
  g.restore();
}

/** A rib built from bands stacked away from an edge. `pts` is the edge;
 *  `n(i)` is the unit direction the bands grow in at each point. */
function rib(g: G, pts: Pt[], normal: (i: number) => Pt, t: number, bands: KitBand[]): void {
  const total = bands.reduce((a, b) => a + b[1], 0);
  let at = 0;
  g.lineJoin = 'round';
  g.lineCap = 'butt';
  for (const [color, weight] of bands) {
    const w = (weight / total) * t;
    const o = at + w / 2;
    g.strokeStyle = css(color);
    g.lineWidth = w + 0.006;
    g.beginPath();
    pts.forEach(([x, y], i) => {
      const nn = normal(i);
      if (i === 0) g.moveTo(x + nn[0] * o, y + nn[1] * o);
      else g.lineTo(x + nn[0] * o, y + nn[1] * o);
    });
    g.stroke();
    at += w;
  }
}

/** Cuffs: a rib across each sleeve end, hem band first. */
function paintCuffs(g: G, spec: KitSpec, shirt: Path2D): void {
  const counter = spec.pattern === 'quarters';
  if (spec.cuffs === undefined && !counter) return;
  g.save();
  g.clip(shirt);
  for (const s of [-1, 1]) {
    const bands: KitBand[] = counter
      ? [[s < 0 ? spec.colors[0] : spec.colors[1], 1]]
      : (spec.cuffBands ?? [[spec.cuffs!, 1]]);
    const dx = CUFF_TOP[0] - CUFF_BOT[0];
    const dy = CUFF_TOP[1] - CUFF_BOT[1];
    const len = Math.hypot(dx, dy);
    // Inward (toward the shoulder), from the hem.
    const nx = -dy / len;
    const ny = dx / len;
    const a: Pt = [s * (CUFF_TOP[0] + dx * 0.25), CUFF_TOP[1] + dy * 0.25];
    const b: Pt = [s * (CUFF_BOT[0] - dx * 0.25), CUFF_BOT[1] - dy * 0.25];
    rib(g, [a, b], () => [s * nx, ny], 0.17, bands);
  }
  g.restore();
}

/** Cloth: light from the upper left, folds off the armpits, a seam. */
function paintCloth(g: G, spec: KitSpec, shirt: Path2D): void {
  const pale = luma(spec.colors[0]) > 0.55;
  const shade = pale ? 0.17 : 0.3;
  g.save();
  g.clip(shirt);
  // Broad light: upper-left lit, lower-right in shade.
  const lit = g.createLinearGradient(-1.3, 0, 1.5, HEM);
  lit.addColorStop(0, rgba(0xffffff, pale ? 0.08 : 0.16));
  lit.addColorStop(0.45, rgba(0xffffff, 0));
  lit.addColorStop(1, rgba(0x000000, shade));
  g.fillStyle = lit;
  g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
  // Side shading rounds the torso (it fades in below the armpits).
  for (const s of [-1, 1]) {
    g.save();
    g.beginPath();
    g.rect(s > 0 ? 0 : -1.3, ARMPIT[1] - 0.25, 1.3, HEM);
    g.clip();
    const side = g.createRadialGradient(s * 1.25, 1.75, 0.1, s * 1.25, 1.75, 1.05);
    side.addColorStop(0, rgba(0x000000, s > 0 ? shade : shade * 0.55));
    side.addColorStop(1, rgba(0x000000, 0));
    g.fillStyle = side;
    g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
    g.restore();
  }
  // Folds: soft dark creases falling from each armpit, a lift beside them.
  const fold = (x0: number, y0: number, x1: number, y1: number, w: number, a: number, white = false): void => {
    const grad = g.createLinearGradient(x0, y0, x1, y1);
    const c = white ? 0xffffff : 0x000000;
    grad.addColorStop(0, rgba(c, a));
    grad.addColorStop(1, rgba(c, 0));
    g.strokeStyle = grad;
    g.lineWidth = w;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(x0, y0);
    g.quadraticCurveTo((x0 + x1) / 2 + (x1 - x0) * 0.1, (y0 + y1) / 2 - 0.12, x1, y1);
    g.stroke();
  };
  for (const s of [-1, 1]) {
    fold(s * 0.96, 0.97, s * 0.42, 1.58, 0.15, shade * 0.5);
    fold(s * 0.9, 1.08, s * 0.5, 1.98, 0.09, shade * 0.36);
    fold(s * 0.78, 1.0, s * 0.3, 1.4, 0.09, pale ? 0.5 : 0.12, true);
    fold(s * 1.12, 0.52, s * 1.5, 1.06, 0.09, shade * 0.36); // sleeve crease
  }
  // Chest sheen.
  const sheen = g.createRadialGradient(-0.32, 0.8, 0, -0.32, 0.8, 0.95);
  sheen.addColorStop(0, rgba(0xffffff, pale ? 0.3 : 0.13));
  sheen.addColorStop(1, rgba(0xffffff, 0));
  g.fillStyle = sheen;
  g.fillRect(-FAR, -FAR, 2 * FAR, 2 * FAR);
  // The set-in sleeve seams.
  g.strokeStyle = rgba(0x000000, pale ? 0.2 : 0.34);
  g.lineWidth = 0.02;
  for (const s of [-1, 1]) {
    g.beginPath();
    g.moveTo(s * SHOULDER[0], SHOULDER[1]);
    g.quadraticCurveTo(s * 0.86, 0.57, s * ARMPIT[0], ARMPIT[1]);
    g.stroke();
  }
  // Hem stitch.
  g.strokeStyle = rgba(0x000000, 0.15);
  g.lineWidth = 0.015;
  g.beginPath();
  g.moveTo(-HIP[0], HIP[1] - 0.1);
  g.quadraticCurveTo(0, HEM, HIP[0], HIP[1] - 0.1);
  g.stroke();
  g.restore();
}

/** The back of the neck, seen through the opening: the inside of the shirt
 *  and the collar's back rib. Drawn BEFORE the shirt front. */
function paintNeckBack(g: G, spec: KitSpec): void {
  const [base] = spec.colors;
  const collar = spec.collar === false ? undefined : (spec.collar ?? spec.accent);
  const inner = mixColor(base, 0x000000, luma(base) > 0.6 ? 0.2 : 0.45);
  const d = neckDepth(spec);
  g.fillStyle = css(inner);
  g.beginPath();
  g.moveTo(-NECK[0], NECK[1]);
  g.bezierCurveTo(-0.3, -0.1, 0.3, -0.1, NECK[0], NECK[1]);
  g.lineTo(NECK[0], d + 0.05);
  g.lineTo(-NECK[0], d + 0.05);
  g.closePath();
  g.fill();
  // Soft shadow inside the opening.
  const sh = g.createLinearGradient(0, -0.08, 0, d);
  sh.addColorStop(0, rgba(0x000000, 0));
  sh.addColorStop(1, rgba(0x000000, 0.35));
  g.fillStyle = sh;
  g.fill();
  // The collar's back rib along the top of the opening.
  const ribColour = collar ?? mixColor(base, 0x000000, 0.08);
  g.strokeStyle = css(mixColor(ribColour, 0x000000, 0.14));
  g.lineWidth = 0.075;
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(-NECK[0], NECK[1]);
  g.bezierCurveTo(-0.3, -0.1, 0.3, -0.1, NECK[0], NECK[1]);
  g.stroke();
}

/** The collar's front: the rib round the neckline, or the polo / open
 *  collar's leaves (with the V or the placket beneath them). */
function paintCollar(g: G, spec: KitSpec): void {
  const [base] = spec.colors;
  const collar = spec.collar === false ? undefined : (spec.collar ?? spec.accent);
  const neck = spec.neck ?? 'crew';
  const d = neckDepth(spec);
  const main = collar ?? mixColor(base, 0x000000, 0.1);
  const bands: KitBand[] = spec.collarBands ?? [[main, 1]];
  const t = 0.13;
  const nl = necklinePts(spec);
  // Outward normal of the neckline at sample i (down and away from the neck).
  const normal = (pts: Pt[]) => (i: number): Pt => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    const tx = b[0] - a[0];
    const ty = b[1] - a[1];
    const len = Math.hypot(tx, ty) || 1;
    // At a V's point the two arms' normals average to straight down.
    return [-ty / len, tx / len];
  };

  if (neck === 'polo' || neck === 'open') {
    if (neck === 'open') {
      // The plain V under the collar.
      const vee = spec.vneck ?? mixColor(base, 0x000000, 0.2);
      rib(g, nl, normal(nl), 0.1, [[vee, 1]]);
    } else {
      // The placket and its buttons.
      const plk = spec.placket ?? mixColor(base, 0x000000, 0.12);
      g.fillStyle = css(plk);
      g.fillRect(-0.075, d - 0.02, 0.15, 0.5);
      g.strokeStyle = rgba(0x000000, 0.25);
      g.lineWidth = 0.012;
      g.strokeRect(-0.075, d - 0.02, 0.15, 0.5);
      g.fillStyle = css(luma(plk) > 0.6 ? 0x22252d : 0xf2f4f8);
      for (const y of spec.buttons === 3 ? [d + 0.07, d + 0.21, d + 0.35] : [d + 0.1, d + 0.3]) {
        g.beginPath();
        g.arc(0, y, 0.028, 0, Math.PI * 2);
        g.fill();
      }
    }
    // Two leaves folded down over the shoulders.
    const tips = bands.slice(1).reverse();
    const dl = neck === 'open' ? d * 0.62 : d;
    for (const s of [-1, 1]) {
      const leaf: Pt[] = [
        [s * NECK[0] * 0.98, NECK[1] - 0.03],
        [s * 0.03, dl],
        [s * 0.37, dl + 0.26],
        [s * 0.66, 0.13],
      ];
      g.fillStyle = css(bands[0][0]);
      g.beginPath();
      leaf.forEach(([x, y], i) => (i === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
      g.closePath();
      g.fill();
      // Tipping along the free edge, outermost first.
      tips.forEach(([c], i) => {
        const o = 0.022 + i * 0.04;
        g.strokeStyle = css(c);
        g.lineWidth = 0.034;
        g.lineJoin = 'miter';
        g.beginPath();
        g.moveTo(s * (0.03 + o * 0.9), dl - o * 0.15);
        g.lineTo(s * (0.37 - o * 0.1), dl + 0.26 - o * 1.35);
        g.lineTo(s * (0.66 - o * 1.1), 0.13 + o * 0.25);
        g.stroke();
      });
      // The leaf's shadow on the shirt and its folded edge.
      g.strokeStyle = rgba(0x000000, luma(bands[0][0]) > 0.7 ? 0.22 : 0.4);
      g.lineWidth = 0.018;
      g.lineJoin = 'round';
      g.beginPath();
      g.moveTo(s * 0.03, dl);
      g.lineTo(s * 0.37, dl + 0.26);
      g.lineTo(s * 0.66, 0.13);
      g.stroke();
    }
    return;
  }

  if (spec.collarHalves) {
    const half = Math.floor(nl.length / 2);
    const left = nl.slice(0, half + 1);
    const right = nl.slice(half);
    rib(g, left, normal(left), t, [[spec.collarHalves[0], 1]]);
    rib(g, right, normal(right), t, [[spec.collarHalves[1], 1]]);
  } else if (neck === 'wrap') {
    // Wrap-over: the viewer's left arm first, the right arm laid across it.
    const left: Pt[] = [nl[0], [nl[1][0] + 0.06, nl[1][1] + 0.05]];
    const right: Pt[] = [[nl[1][0] - 0.09, nl[1][1] + 0.07], nl[2]];
    rib(g, left, normal(left), t, bands);
    rib(g, right, normal(right), t, bands);
    g.strokeStyle = rgba(0x000000, 0.28);
    g.lineWidth = 0.014;
    g.beginPath();
    g.moveTo(right[0][0], right[0][1]);
    g.lineTo(right[0][0] + 0.09, right[0][1] - 0.12);
    g.stroke();
  } else {
    rib(g, nl, normal(nl), t, bands);
  }
}

/**
 * The kit as a floating jersey. Transparent canvas (the card's own glow shows
 * round it); `w × h` are pixels — pass about 3× the tile for retina.
 */
export function kitCardCanvas(spec: KitSpec, w = 360, hgt = 240): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const g = c.getContext('2d')!;
  // Shirt units → pixels: fit the tilted shirt to the card with a breath of air.
  const unit = Math.min(hgt / 3.02, w / 4.15);
  const place = (ctx: G): void => {
    ctx.translate(w / 2 + unit * 0.05, hgt / 2 - unit * 1.07);
    ctx.rotate(-0.1); // a slight, relaxed tilt
    ctx.scale(unit, unit);
  };
  const shirt = outline(spec);

  // Soft ground shadow: the shirt hovers off the card.
  g.save();
  place(g);
  g.shadowColor = 'rgba(0,0,0,0.6)';
  g.shadowBlur = unit * 0.28;
  g.shadowOffsetX = unit * 0.05;
  g.shadowOffsetY = unit * 0.15;
  g.fillStyle = '#000';
  g.fill(shirt);
  g.restore();

  g.save();
  place(g);
  paintNeckBack(g, spec);
  paintField(g, spec, shirt);
  paintCuffs(g, spec, shirt);
  paintCloth(g, spec, shirt);
  paintCollar(g, spec);
  // A hairline rim so dark shirts hold their edge on a dark card.
  g.strokeStyle = rgba(0xffffff, luma(spec.colors[0]) > 0.55 ? 0.34 : 0.26);
  g.lineWidth = 0.018;
  g.stroke(shirt);
  g.restore();
  return c;
}
